import React, { useState, useRef, useEffect, useMemo, useCallback, memo } from "react";
import { createPortal } from "react-dom";
import { OptionType } from "@shared/types";
import { useThemeStore } from "@shared/stores";
import Tooltip from "./Tooltip";
import Input from "./Input";
import { ChevronDown, MagnifyingGlass } from "../icons/IconCustom";

type Props = {
  options?: OptionType[];
  Children?: React.ComponentType<{ onClose?: () => void; maxHeight?: number }>;
  icon?: React.ReactNode;
  value?: string | number | boolean;
  onChange?: (value: string | number | boolean) => void;
  placeholder?: string;
  className?: string;
  sizeArrowDown?: string;
  isContentCenter?: boolean;
  widthDropdown?: number;
  maxHeightDropdown?: number;
  isShowSelected?: boolean;
  isHighlightSelected?: boolean;
  tooltipMessage?: string;
  tooltipMaxLines?: number;
  isSearch?: boolean;
  alignTo?: "left" | "right";
  defaultOpen?: boolean;
  isNotBorder?: boolean;
  fontSize?: string;
  maxWidthDropdown?: number;
};

const Select = ({
  options,
  Children,
  icon,
  value,
  onChange,
  placeholder,
  sizeArrowDown,
  className,
  isContentCenter,
  widthDropdown,
  maxHeightDropdown = 300,
  isShowSelected = true,
  isHighlightSelected = false,
  tooltipMessage,
  tooltipMaxLines,
  isSearch,
  alignTo = "left",
  defaultOpen = false,
  isNotBorder = false,
  fontSize = "text-xs",
  maxWidthDropdown,
}: Props) => {
  const { isDarkMode } = useThemeStore();

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement | HTMLUListElement>(null);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isMeasured, setIsMeasured] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    width: 0,
    showAbove: false,
    alignRight: false,
  });
  const selectedOption = useMemo(() => options?.find((option) => option.value === value) || null, [options, value]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setIsMeasured(false);
  }, []);

  const calculateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate left position based on alignTo
    let left: number;
    let alignRight = false;

    if (alignTo === "right") {
      // Align to right edge of container
      alignRight = true;
      // left will not be used when alignRight is true, we use right property instead
      left = 0; // placeholder
    } else {
      // Align to left edge of container (default) - always align to container.left
      left = container.left;
      alignRight = false;
    }

    // oy
    let actualHeight = maxHeightDropdown;
    if (dropdownRef.current) {
      actualHeight = dropdownRef.current.offsetHeight;
    } else if (options && options.length > 0) {
      const optionHeight = 32;
      const estimatedContentHeight = options.length * optionHeight;
      actualHeight = Math.min(estimatedContentHeight + 16, maxHeightDropdown);
    }

    const spaceBelow = viewportHeight - container.bottom;
    const isShowAbove = spaceBelow < actualHeight;
    const bottom = isShowAbove ? viewportHeight - container.top : 0;
    const top = isShowAbove ? 0 : container.bottom;
    setDropdownPosition({
      top,
      bottom,
      left,
      width: widthDropdown || 0, // 0 means auto
      showAbove: isShowAbove,
      alignRight,
    });
    setIsMeasured(true);
  }, [options, maxHeightDropdown, widthDropdown, alignTo]);

  const handleOpenDropdown = () => {
    if (!isOpen) {
      // Calculate position before opening
      setIsOpen(true);
      // Position will be calculated in useEffect below
    } else {
      setIsOpen(false);
      setIsMeasured(false);
    }
  };

  const dropdownContent = useMemo((): React.ReactNode | null => {
    if (!isOpen) return null;

    const viewportWidth = window.innerWidth;
    const maxWidth = maxWidthDropdown ? maxWidthDropdown : viewportWidth - 40; // Max width with padding
    const containerWidth = containerRef.current?.getBoundingClientRect().width ?? 0;

    const dropdownStyle = {
      position: "fixed" as const,
      width: widthDropdown ? dropdownPosition.width : "",
      minWidth: containerWidth > 0 ? containerWidth : undefined,
      maxWidth: maxWidth,
      zIndex: 99999,
      maxHeight: maxHeightDropdown,
      visibility: isMeasured ? "visible" : "hidden",
      opacity: isMeasured ? 1 : 0,
    } as React.CSSProperties;

    // Use right for right alignment, left for left alignment
    const container = containerRef.current?.getBoundingClientRect();
    if (dropdownPosition.alignRight) {
      // Align to right edge of container
      if (container) {
        dropdownStyle.right = viewportWidth - container.right;
      }
    } else {
      // Align to left edge of container - always use current container.left
      if (container) {
        dropdownStyle.left = container.left;
      } else {
        dropdownStyle.left = dropdownPosition.left;
      }
    }

    if (dropdownPosition.showAbove) {
      dropdownStyle.bottom = dropdownPosition.bottom;
    } else {
      dropdownStyle.top = dropdownPosition.top;
    }

    const content = (() => {
      if (Children) {
        return (
          <div
            ref={dropdownRef as React.RefObject<HTMLDivElement>}
            data-select-dropdown
            className="overflow-x-auto overflow-y-auto rounded-[4px] bg-white shadow-elevationShadow4 dark:bg-black"
            style={dropdownStyle}
          >
            <div className="min-w-0">
              <Children onClose={closeDropdown} maxHeight={maxHeightDropdown} />
            </div>
          </div>
        );
      }

      if (options && options.length > 0) {
        return (
          <ul
            ref={dropdownRef as React.RefObject<HTMLUListElement>}
            data-select-dropdown
            className={`absolute overflow-x-auto overflow-y-auto rounded-[4px] border bg-white shadow-elevationShadow4 dark:bg-black dark:border-none`}
            style={dropdownStyle}
          >
            {isSearch ? (
              <li className="border-b dark:border-midGray p-1">
                <Input
                  placeholder="Search"
                  leftIcon={<MagnifyingGlass isDarkMode={isDarkMode} />}
                  className="border-none h-[32px] text-sm dark:text-white"
                />
              </li>
            ) : null}
            {options.map((option) => (
              <li
                key={option.value as string}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.(option.value);
                  closeDropdown();
                }}
                className={`px-3 py-2 ${fontSize} cursor-pointer hover:bg-extraLightGray dark:hover:bg-deepGray dark:text-white whitespace-nowrap
                  ${
                    option.value === value
                      ? "bg-softBlue hover:bg-softBlue dark:bg-midnightBlue dark:hover:bg-midnightBlue"
                      : ""
                  }
                  ${option.color ? "flex items-center gap-2" : ""}`}
              >
                {option.color ? <div className="w-4 h-4" style={{ backgroundColor: option.color }} /> : null}
                {option.label}
              </li>
            ))}
          </ul>
        );
      }
      return null;
    })();

    return content ? createPortal(content, document.body) : null;
  }, [
    isOpen,
    isMeasured,
    Children,
    options,
    dropdownPosition,
    maxHeightDropdown,
    widthDropdown,
    value,
    onChange,
    closeDropdown,
  ]);

  useEffect(() => {
    if (isOpen) {
      const handleUpdate = () => calculateDropdownPosition();
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (containerRef.current?.contains(target)) {
          return;
        }
        const dropdownElements = document.querySelectorAll("[data-select-dropdown]");
        for (const element of dropdownElements) {
          if (element.contains(target)) {
            return;
          }
        }
        closeDropdown();
      };
      window.addEventListener("scroll", handleUpdate, true);
      window.addEventListener("resize", handleUpdate);
      window.addEventListener("mousedown", handleClickOutside);

      return () => {
        window.removeEventListener("scroll", handleUpdate, true);
        window.removeEventListener("resize", handleUpdate);
        window.removeEventListener("mousedown", handleClickOutside);
      };
    }
    return undefined;
  }, [isOpen, calculateDropdownPosition]);

  useEffect(() => {
    if (isOpen) {
      // Calculate position immediately when opening
      requestAnimationFrame(() => {
        calculateDropdownPosition();
      });
    } else {
      setIsMeasured(false);
    }
  }, [isOpen, calculateDropdownPosition]);

  const selectComponent = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        handleOpenDropdown();
      }}
      className={`${isNotBorder ? "px-0" : "px-2"} w-full h-full flex gap-1 items-center ${isContentCenter ? (isNotBorder ? "justify-start" : "justify-center") : "justify-between"}`}
    >
      <span
        className={`flex items-center gap-[2px] truncate ${
          isHighlightSelected ? "text-primaryBlue dark:text-brightBlue" : ""
        }`}
      >
        {icon ? icon : null}
        {selectedOption && selectedOption?.color ? (
          <div className="w-4 h-4 mr-1" style={{ backgroundColor: selectedOption.color }} />
        ) : null}
        <span className={`truncate ${fontSize}`}>
          {isShowSelected && selectedOption ? selectedOption?.label || placeholder : placeholder}
        </span>
      </span>
      <ChevronDown
        isDarkMode={isDarkMode}
        size={12}
        className={`${sizeArrowDown ? sizeArrowDown : ""} ${isOpen ? "rotate-180" : "rotate-0"}`}
      />
    </button>
  );

  const baseClasses = isNotBorder ? "relative" : "relative rounded-[4px] border-[1px] border-solid border-mediumGray dark:border-midGray";
  const backgroundClasses = useMemo(
    () => (className?.includes("bg-") || className?.includes("dark:bg-") ? "" : "bg-white dark:bg-darkBackgroundColor"),
    [className]
  );

  return (
    <div ref={containerRef} className={`${baseClasses} ${backgroundClasses} ${className || ""}`}>
      {tooltipMessage ? (
        <Tooltip message={tooltipMessage} className="w-full h-full" position="top" maxLines={tooltipMaxLines}>
          {selectComponent}
        </Tooltip>
      ) : (
        selectComponent
      )}

      {dropdownContent}
    </div>
  );
};

export default memo(Select);
